import React, { FC, useState } from 'react';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { CentsDataGrid } from 'components/common/dataGrid/dataGrid';
import { IndividualPayPalMerchantAccount } from 'api/admin/businesses/getPayPalMerchantAccounts';
import { ConfirmPayPalActionDialog } from './ConfirmPayPalActionDialog';
import styles from './MerchantAccounts.module.scss';
import { PayPalLoadError } from './PayPalLoadError';
import { useDeletePayPalMerchantAccount } from './mutations/useDeletePayPalMerchantAccount';
import { getPayPalPayerIdLabel, getPayPalTransactionsReadyLabel } from './paypalAccountLabels';
import { usePayPalMerchantAccounts } from './queries/usePayPalMerchantAccounts';

export const PayPalMerchantAccounts: FC<{
  businessId: number;
}> = ({ businessId }) => {
  const {
    isLoading,
    error,
    data: merchantAccounts,
    refetch,
  } = usePayPalMerchantAccounts(businessId);
  const { mutateAsync: deleteMerchantAccount, isLoading: isDeleting } =
    useDeletePayPalMerchantAccount();
  const [accountToRemove, setAccountToRemove] = useState<IndividualPayPalMerchantAccount | null>(
    null,
  );

  const handleConfirmRemove = async () => {
    if (!accountToRemove) {
      return;
    }

    try {
      await deleteMerchantAccount({
        businessId,
        paypalMerchantAccountId: accountToRemove.id,
      });
      setAccountToRemove(null);
      await refetch();
    } catch {
      // The mutation's onError has already surfaced the failure; keep the dialog open to retry.
    }
  };

  const columns = [
    { headerName: 'ID', field: 'id', flex: 1 },
    {
      headerName: 'Payer Id',
      field: 'payerId',
      flex: 4,
      valueGetter: ({ row }: { row: IndividualPayPalMerchantAccount }) =>
        getPayPalPayerIdLabel(row.payerId),
    },
    {
      headerName: 'Transactions',
      field: 'transactionsReady',
      flex: 2,
      valueGetter: ({ row }: { row: IndividualPayPalMerchantAccount }) =>
        getPayPalTransactionsReadyLabel(row.transactionsReady),
    },
    { headerName: 'Assigned Stores', field: 'assignedStoreCount', flex: 2 },
    {
      headerName: '',
      field: 'actions',
      flex: 2,
      sortable: false,
      filterable: false,
      renderCell: ({ row }: GridRenderCellParams<IndividualPayPalMerchantAccount>) => {
        // CentsBE refuses to remove an account stores still route to; unassign them first.
        const isInUse = row.assignedStoreCount > 0;

        return (
          <Tooltip
            title={isInUse ? 'Unassign this account from every store before removing it' : ''}
          >
            {/* A disabled button gets no pointer events, so the span is what shows the tooltip. */}
            <span>
              <Button
                color="error"
                size="small"
                disabled={isInUse}
                onClick={() => setAccountToRemove(row)}
              >
                Remove
              </Button>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div className="page-container">
      <p className={styles.description}>
        Merchants connect PayPal accounts from Business Manager. Assign an account to a store from
        the store&apos;s PayPal Account tab. An account can only be removed once no store is
        assigned to it.
      </p>
      {error ? (
        <PayPalLoadError
          message="Couldn't load this business's PayPal merchant accounts."
          onRetry={() => refetch()}
        />
      ) : (
        <CentsDataGrid
          tableName="paypalMerchantAccounts"
          data={merchantAccounts}
          columns={columns}
          isLoading={isLoading}
        />
      )}
      <ConfirmPayPalActionDialog
        open={!!accountToRemove}
        title="Remove PayPal merchant account?"
        confirmLabel="Remove"
        isSubmitting={isDeleting}
        onCancel={() => setAccountToRemove(null)}
        onConfirm={handleConfirmRemove}
      >
        {`Merchant account ${accountToRemove?.id} (${getPayPalPayerIdLabel(
          accountToRemove?.payerId ?? null,
        )}) will be removed from this business.`}
      </ConfirmPayPalActionDialog>
    </div>
  );
};
